import { useState, useEffect, useId } from 'react';
import { FileDownIcon } from 'lucide-react';
import type { Endpoint } from '@mockd/shared';
import type { Rule } from '@mockd/shared/types/rule';
import { endpointToCurl, endpointMethods, endpointsToJson, endpointsToOpenApi, downloadJson } from '../../utils/exportEndpoint';
import { api } from '../../utils/apiClient';
import { Alert, CopyButton, Loading, TabList, tabPanelProps } from '../common';
import { Modal } from '../common/Modal';

type ExportFormat = 'curl' | 'json' | 'openapi';

const FORMAT_TABS: { key: ExportFormat; label: string }[] = [
  { key: 'curl', label: 'cURL' },
  { key: 'json', label: 'JSON' },
  { key: 'openapi', label: 'OpenAPI' },
];

interface ExportEndpointModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  baseUrl: string;
  endpoints: Endpoint[];
  /** If set, only export this single endpoint */
  singleEndpointId?: string;
}

export function ExportEndpointModal({
  isOpen,
  onClose,
  projectId,
  projectName,
  baseUrl,
  endpoints,
  singleEndpointId,
}: ExportEndpointModalProps) {
  const [format, setFormat] = useState<ExportFormat>('curl');
  const tabsId = useId();
  const [rulesMap, setRulesMap] = useState<Map<string, Rule[]>>(new Map());
  const [loadingRules, setLoadingRules] = useState(false);
  // Paths whose rules failed to load. Any export built without them would be
  // missing rules while looking complete, so export is blocked until a retry.
  const [failedPaths, setFailedPaths] = useState<string[]>([]);
  const [fetchAttempt, setFetchAttempt] = useState(0);

  const targetEndpoints = singleEndpointId
    ? endpoints.filter(e => e.id === singleEndpointId)
    : endpoints;

  // Fetch rules for all target endpoints when modal opens
  useEffect(() => {
    if (!isOpen) {
      setFormat('curl');
      setRulesMap(new Map());
      setFailedPaths([]);
      return;
    }

    let cancelled = false;
    async function fetchAllRules() {
      setLoadingRules(true);
      setFailedPaths([]);
      const map = new Map<string, Rule[]>();
      const failed: string[] = [];
      await Promise.all(
        targetEndpoints.map(async (ep) => {
          try {
            const json = await api.get<{ data: Rule[] }>(
              `/api/projects/${projectId}/endpoints/${ep.id}/rules`
            );
            map.set(ep.id, json.data || []);
          } catch {
            failed.push(ep.path);
          }
        })
      );
      if (!cancelled) {
        setRulesMap(map);
        setFailedPaths(failed);
        setLoadingRules(false);
      }
    }

    fetchAllRules();
    return () => { cancelled = true; };
  }, [isOpen, projectId, fetchAttempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportBlocked = loadingRules || failedPaths.length > 0;

  const getCurlCommands = (): string => {
    return targetEndpoints
      .flatMap(ep => endpointMethods(rulesMap.get(ep.id) ?? []).map(method => endpointToCurl(baseUrl, ep, method)))
      .join('\n\n');
  };

  const getJsonExport = (): string => {
    const data = endpointsToJson(projectName, targetEndpoints, rulesMap);
    return JSON.stringify(data, null, 2);
  };

  const getOpenApiExport = (): string => {
    const data = endpointsToOpenApi(projectName, baseUrl, targetEndpoints, rulesMap);
    return JSON.stringify(data, null, 2);
  };

  const getExportContent = (): string => {
    switch (format) {
      case 'curl': return getCurlCommands();
      case 'json': return getJsonExport();
      case 'openapi': return getOpenApiExport();
    }
  };

  const handleDownload = () => {
    const slug = projectName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    switch (format) {
      case 'json':
        downloadJson(
          endpointsToJson(projectName, targetEndpoints, rulesMap),
          `${slug}-endpoints.json`
        );
        break;
      case 'openapi':
        downloadJson(
          endpointsToOpenApi(projectName, baseUrl, targetEndpoints, rulesMap),
          `${slug}-openapi.json`
        );
        break;
      case 'curl': {
        const blob = new Blob([getCurlCommands()], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${slug}-curl.sh`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        break;
      }
    }
  };

  const title = singleEndpointId
    ? `Export Endpoint`
    : `Export ${targetEndpoints.length} Endpoint${targetEndpoints.length !== 1 ? 's' : ''}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      titleClassName="font-bold text-lg mb-4"
      boxClassName="max-w-2xl"
    >
      {/* Format tabs */}
      <TabList
        tabs={FORMAT_TABS}
        active={format}
        onChange={setFormat}
        idPrefix={tabsId}
        className="tabs tabs-border mb-4"
      />

      {/* Export preview */}
      <div {...tabPanelProps(tabsId, format)} className="relative">
        {loadingRules ? (
          <Loading label="Loading rules…" className="bg-base-200 p-4 rounded-box h-32" />
        ) : failedPaths.length > 0 ? (
          <Alert
            variant="error"
            action={
              <button className="btn btn-sm" onClick={() => setFetchAttempt(n => n + 1)}>
                Retry
              </button>
            }
          >
            Couldn't load rules for {failedPaths.join(', ')}, so the export would be incomplete.
          </Alert>
        ) : (
          <>
            <pre className="bg-base-200 p-4 rounded-box text-xs font-mono overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap break-all">
              {getExportContent()}
            </pre>
            <div className="absolute top-2 right-2">
              <CopyButton text={getExportContent()} label="Copy" className="btn-xs" />
            </div>
          </>
        )}
      </div>

      {format === 'curl' && (
        <p className="text-xs text-base-content/70 mt-2">
          cURL commands you can run directly in your terminal to test the mock endpoints.
        </p>
      )}
      {format === 'json' && (
        <p className="text-xs text-base-content/70 mt-2">
          mockd export format. Import this into another mockd project to recreate these endpoints.
        </p>
      )}
      {format === 'openapi' && (
        <p className="text-xs text-base-content/70 mt-2">
          OpenAPI 3.0 spec. Import into Postman, Swagger UI, or any OpenAPI-compatible tool.
        </p>
      )}

      {/* Actions */}
      <div className="modal-action">
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Close
        </button>
        <button className="btn btn-outline" onClick={handleDownload} disabled={exportBlocked}>
          <FileDownIcon className="size-4" />
          Download
        </button>
      </div>
    </Modal>
  );
}
